// src/integrations/providers/bitly/bitlyFormPortability.js

import {
  sanitizeTableSurfaceProviderColumnsForDuplicate,
} from '../../formPortability/providerColumnPortabilityUtils';

const PROVIDER_ID = 'bitly';

function isPlainObject(value) {
  return Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);
}

function hasBitlyActionState(action = {}) {
  return (
    Object.prototype.hasOwnProperty.call(action, 'enableBitly') ||
    Object.prototype.hasOwnProperty.call(action, 'bitlyDestinationUfi') ||
    action?.shortlinkProvider === PROVIDER_ID ||
    action?.shortlinkDestination?.provider === PROVIDER_ID ||
    action?.shortlinkDisplay?.provider === PROVIDER_ID
  );
}

function sanitizeBitlyActionState(action = {}) {
  if (!isPlainObject(action)) {
    return {
      action,
      diagnostics: {
        removedSettingKeys: [],
        removedColumnIds: [],
      },
      changed: false,
    };
  }

  const columnResult = sanitizeTableSurfaceProviderColumnsForDuplicate(
    action,
    PROVIDER_ID
  );

  let nextAction = columnResult.surface;
  const removedSettingKeys = [];

  if (hasBitlyActionState(nextAction)) {
    const mutableAction = {
      ...nextAction,
    };

    [
      'enableBitly',
      'bitlyDestinationUfi',
      'shortlinkProvider',
      'shortlinkDestination',
      'shortlinkDisplay',
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(mutableAction, key)) {
        delete mutableAction[key];
        removedSettingKeys.push(key);
      }
    });

    nextAction = mutableAction;
  }

  return {
    action: nextAction,
    diagnostics: {
      removedSettingKeys,
      removedColumnIds: columnResult.removedColumnIds,
    },
    changed:
      removedSettingKeys.length > 0 ||
      columnResult.changed === true,
  };
}

export const bitlyFormPortabilityPolicy = Object.freeze({
  providerId: PROVIDER_ID,
  duplicatePolicy: 'strip',

  sanitizeSubmissionSettingsForDuplicate(submissionSettings = {}) {
    if (!isPlainObject(submissionSettings)) {
      return {
        settings: submissionSettings,
        diagnostics: {
          changed: false,
          touchedSettingsKeys: [],
          removedSettingKeys: [],
          removedColumnIds: [],
        },
      };
    }

    const nextSettings = {
      ...submissionSettings,
    };

    const touchedSettingsKeys = [];
    const removedSettingKeys = [];
    const removedColumnIds = [];

    Object.entries(submissionSettings).forEach(([settingsKey, value]) => {
      const result = sanitizeBitlyActionState(value);

      if (!result.changed) {
        return;
      }

      nextSettings[settingsKey] = result.action;
      touchedSettingsKeys.push(settingsKey);
      removedSettingKeys.push(
        ...result.diagnostics.removedSettingKeys.map((key) => (
          `${settingsKey}.${key}`
        ))
      );
      removedColumnIds.push(...result.diagnostics.removedColumnIds);
    });

    return {
      settings: nextSettings,
      diagnostics: {
        changed: touchedSettingsKeys.length > 0,
        touchedSettingsKeys,
        removedSettingKeys,
        removedColumnIds,
      },
    };
  },
});